import { useEventInfo } from '@shared/utils/eventInfo';
import { useEffect } from 'react';
import { addAction, addError } from '@shared/utils/logger';
import { useGeocoding } from '@shared/hooks/useGeocoding';
import { useAccommodationsConsoleStateInitializationQuery } from '@graphql/generated';
export const useBestAnchorLocation = ({
  onCompleted,
  skip,
}: {
  onCompleted: (data: { latitude: string; longitude: string; friendlyName: string } | null) => void;
  skip: boolean;
}) => {
  const { eventInfo, eventHandle, loading: eventInfoLoading } = useEventInfo();
  const { data, loading, error } = useAccommodationsConsoleStateInitializationQuery({
    variables: {
      name: eventHandle || '',
    },
    onCompleted: data => {
      if (!data?.eventByName?.bestLocationAnchor) {
        addAction('Best anchor location null (AccommodationsConsole)', {
          context: 'bestLocationAnchor',
          eventHandle,
        });
      }
      onCompleted(data?.eventByName?.bestLocationAnchor || null);
    },
    onError: error => {
      addError(error, {
        source: 'Best anchor location api failed (AccommodationsConsole)',
        context: 'bestLocationAnchor',
        errorMessage: error.message,
        eventHandle,
      });
    },
    batchMode: 'off',
    skip: skip || eventInfoLoading,
  });

  const shouldGeocode = Boolean(data?.eventByName && !data.eventByName.bestLocationAnchor && eventInfo?.locationInfo?.name);
  const locationName = eventInfo?.locationInfo?.name || '';
  const { error: geocodeError, result } = useGeocoding({
    address: locationName,
    skip: skip || !shouldGeocode,
  });

  useEffect(() => {
    if (geocodeError) {
      addError(geocodeError, {
        source: 'Failed to geocode location',
        context: 'getPlaceFromLocationService',
        errorMessage: geocodeError,
        locationName,
      });
    }
  }, [geocodeError, locationName]);

  useEffect(() => {
    if (result) {
      onCompleted({
        latitude: result.location.lat.toString(),
        longitude: result.location.lng.toString(),
        friendlyName: locationName || '',
      });
    }
  }, [result, onCompleted, locationName]);

  return { bestAnchorLocation: data?.eventByName?.bestLocationAnchor, loading, error };
};
